import { useEffect, useState } from 'react';
import { reference, type LocalStop } from '../lib/localdb.js';
import { addCapture, addHazard } from '../lib/actions.js';
import { text } from '../lib/format.js';
import { BigButton, CameraButton, Chip, Sheet, SignaturePad } from '../components/Controls.js';

/**
 * Safety.
 *
 * A hazard is not the same thing as a visit failing, and this sheet keeps the two apart. The
 * first question it asks is *who is this dangerous to*, because the answer decides what the
 * technician does:
 *
 *   - **To the technician** — live wiring, a gas smell, an unstable unit, suspected asbestos.
 *     They stop and leave, and the visit fails with a `safety` reason.
 *   - **To the customer or the premises** — a cracked heat exchanger, carbon monoxide, a
 *     refrigerant leak. They document it; the work may continue and the unit may be left off.
 *
 * Three things this refuses to do, all of them deliberately:
 *
 *   1. **No hazard without evidence.** A photograph and a note, or nothing is filed. A flag
 *      with nothing behind it is a rumour, and it would sit on a dwelling record for years.
 *   2. **No wording of its own.** The severities and the notice the customer signs are rows
 *      the business owns, in the business's French, rendered in **the customer's** language —
 *      not the technician's.
 *   3. **No promise about the office.** Offline it queues like everything else, and the sheet
 *      says so rather than implying somebody has already been told.
 */

type Severity = { id: string; kind: string; name: unknown; notice: unknown };
type Category = { id: string; key: string; name: unknown };

export function HazardSheet({
  open,
  stop,
  onClose,
  onSaved,
}: {
  open: boolean;
  stop: LocalStop;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [severities, setSeverities] = useState<Severity[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);

  const [target, setTarget] = useState<'technician' | 'customer'>('customer');
  const [severityId, setSeverityId] = useState<string | null>(null);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [mediaId, setMediaId] = useState<string | null>(null);
  const [ackMediaId, setAckMediaId] = useState<string | null>(null);
  const [ackName, setAckName] = useState('');
  const [outcome, setOutcome] = useState<'return_visit' | 'quote_repair' | 'refer_other_trade' | 'no_further_action' | null>(null);
  const [signing, setSigning] = useState(false);

  useEffect(() => {
    if (!open) return;
    void (async () => {
      setSeverities(await reference<Severity[]>('hazardSeverities', []));
      setCategories(await reference<Category[]>('hazardCategories', []));
    })();
  }, [open]);

  const severity = severities.find((candidate) => candidate.id === severityId) ?? null;
  const complete = Boolean(severityId && note.trim() && mediaId);

  return (
    <Sheet open={open} title="Signaler un danger" onClose={onClose}>
      <div className="hazard">
        <h4>Dangereux pour qui</h4>
        <div className="chips">
          <Chip tone="danger" on={target === 'technician'} onToggle={() => setTarget('technician')}>
            Moi — j’arrête et je pars
          </Chip>
          <Chip tone="warm" on={target === 'customer'} onToggle={() => setTarget('customer')}>
            Le client ou les lieux
          </Chip>
        </div>

        <h4>Gravité</h4>
        <div className="chips">
          {severities.map((candidate) => (
            <Chip
              key={candidate.id}
              tone={candidate.kind === 'do_not_operate' ? 'danger' : candidate.kind === 'advisory' ? 'neutral' : 'warm'}
              on={severityId === candidate.id}
              onToggle={() => setSeverityId(candidate.id)}
            >
              {text(candidate.name)}
            </Chip>
          ))}
        </div>

        {categories.length > 0 && (
          <>
            <h4>Catégorie</h4>
            <div className="chips">
              {categories.map((candidate) => (
                <Chip key={candidate.id} on={categoryId === candidate.id} onToggle={() => setCategoryId(candidate.id)}>
                  {text(candidate.name)}
                </Chip>
              ))}
            </div>
          </>
        )}

        <label className="field">
          <span>Ce que vous avez trouvé</span>
          <textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} />
        </label>

        <CameraButton
          tone={mediaId ? 'quiet' : 'danger'}
          onFile={async (file) => {
            const saved = await addCapture({ appointmentId: stop.id, kind: 'hazard', blob: file, withPosition: true });
            setMediaId(saved.id);
          }}
        >
          {mediaId ? '✓ Photo du danger' : 'Photo du danger — obligatoire'}
        </CameraButton>

        {/*
          The one document at the door worth a signature, rendered in the customer's language.
          "We told you, on this date" is what the photograph and this signature protect.
        */}
        {target === 'customer' && Boolean(severity?.notice) && severity && (
          <section className="notice">
            <h4>Avis au client</h4>
            <p lang={stop.customer.locale}>{text(severity.notice, stop.customer.locale)}</p>
            {ackMediaId ? (
              <p className="quiet">Signé par {ackName || stop.customer.name}.</p>
            ) : signing ? (
              <SignaturePad
                label={`${stop.customer.name} — ${text(severity.name, stop.customer.locale)}`}
                onDone={async (blob) => {
                  const saved = await addCapture({
                    appointmentId: stop.id,
                    kind: 'signature',
                    blob,
                    caption: ackName || stop.customer.name,
                    withPosition: true,
                  });
                  setAckMediaId(saved.id);
                  setSigning(false);
                }}
              />
            ) : (
              <>
                <label className="field">
                  <span>Nom du signataire</span>
                  <input value={ackName} onChange={(event) => setAckName(event.target.value)} placeholder={stop.customer.name} />
                </label>
                <BigButton tone="quiet" onClick={() => setSigning(true)}>
                  Faire signer l’avis
                </BigButton>
              </>
            )}
          </section>
        )}

        <h4>Ce que ça crée</h4>
        <div className="chips">
          <Chip on={outcome === 'return_visit'} onToggle={() => setOutcome('return_visit')}>
            Retour après correction
          </Chip>
          <Chip on={outcome === 'quote_repair'} onToggle={() => setOutcome('quote_repair')}>
            Soumission pour la réparation
          </Chip>
          <Chip on={outcome === 'refer_other_trade'} onToggle={() => setOutcome('refer_other_trade')}>
            Renvoyer à un autre métier
          </Chip>
          <Chip on={outcome === 'no_further_action'} onToggle={() => setOutcome('no_further_action')}>
            Aucune suite
          </Chip>
        </div>

        {!complete && (
          <p className="quiet">Une photo et une note sont obligatoires : un signalement sans preuve est une rumeur.</p>
        )}

        <BigButton
          tone="danger"
          disabled={!complete}
          onClick={async () => {
            await addHazard(stop.id, {
              target,
              severityId: severityId!,
              categoryId,
              note: note.trim(),
              mediaId: mediaId!,
              acknowledgementMediaId: ackMediaId,
              acknowledgedName: ackName || null,
              outcome,
            });
            await onSaved();
          }}
          hint="Envoyé dès que le signal revient"
        >
          Signaler
        </BigButton>
      </div>
    </Sheet>
  );
}
